import { useState } from "react";
import { formatStoredPath } from "../domain/paths";
import type {
  DirectoryOperationProgress,
  PendingDirectoryOperation,
} from "../workspace/directory_operation";

export interface OperationRecoveryScreenProps {
  readonly pending: PendingDirectoryOperation;
  readonly progress: DirectoryOperationProgress | null;
  readonly workspaceName: string;
  readonly onResume: () => Promise<void>;
  readonly onRollback: () => Promise<void>;
  readonly onSwitchWorkspace: () => void;
}

/** Blocks normal editing until one interrupted filesystem operation is resolved safely. */
export function OperationRecoveryScreen({
  pending,
  progress,
  workspaceName,
  onResume,
  onRollback,
  onSwitchWorkspace,
}: OperationRecoveryScreenProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const operation = pending.operation;

  /** Runs one recovery decision while retaining the screen after a safe failure. */
  const run = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The operation remains recoverable.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="operation-recovery" aria-labelledby="operation-recovery-title">
      <p className="eyebrow">Interrupted workspace operation</p>
      <h1 id="operation-recovery-title">Finish the safe filesystem handoff.</h1>
      <p>
        Shotboard found an interrupted {operation.type} in <strong>{workspaceName}</strong>. The
        source remains canonical until the copied destination passes byte verification.
      </p>
      <dl>
        <div>
          <dt>Source</dt>
          <dd>{formatStoredPath(operation.sourcePath)}</dd>
        </div>
        <div>
          <dt>Destination</dt>
          <dd>{formatStoredPath(operation.destinationPath)}</dd>
        </div>
        <div>
          <dt>Last durable phase</dt>
          <dd>{operation.phase.replaceAll("_", " ")}</dd>
        </div>
      </dl>
      {progress === null ? null : (
        <p role="status">
          {progress.phase.replaceAll("_", " ")}: {progress.completedEntries} of{" "}
          {progress.totalEntries}
        </p>
      )}
      {error === null ? null : (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="welcome-actions">
        <button
          type="button"
          className="primary-action"
          disabled={busy}
          onClick={() => void run(onResume)}
        >
          {busy ? "Working…" : "Resume Safely"}
        </button>
        {pending.canRollback ? (
          <button
            type="button"
            className="secondary-action"
            disabled={busy}
            onClick={() => void run(onRollback)}
          >
            Roll Back Incomplete Copy
          </button>
        ) : null}
        <button
          type="button"
          className="secondary-action"
          disabled={busy}
          onClick={onSwitchWorkspace}
        >
          Switch Workspace
        </button>
      </div>
    </main>
  );
}
